import { type ReactNode } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import ExclamationIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/exclamation';
import {
  BpkTable,
  BpkTableBody,
  BpkTableCell,
  BpkTableRow,
} from '@skyscanner-internal/backpack-web/bpk-component-table';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';
import { parseISO } from 'date-fns/parseISO';

import {
  getScheduledArrivalAirportCode,
  getScheduledArrivalAirportName,
  isDivertedFlight,
} from '../flightStatusUpdate';
import {
  getBestArrivalTime,
  getBestDepartureTime,
  getPreferredTimeField,
} from '../utils';

import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';
import type { FlightStatusDetailsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightStatusDetailsProps';

import STYLES from './FlightInfoTable.module.scss';

type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;

type FlightInfoTableProps = {
  flight: FlightStatusUpdate | null;
  locale: string;
  strings: FlightStatusDetailsProps['strings'];
};

/**
 * Match the YYYY-MM-DD prefix of either a localised (no-TZ) or UTC ISO
 * datetime string. Treating the calendar day as a fixed-UTC date keeps the
 * rendered day invariant across browser timezones, matching the departure-
 * airport local day that the service already encoded in the prefix.
 */
const displayDatePrefixPattern = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Format the departure / arrival date row for user display.
 *
 * Per UAT 3.1 the date shown must match the departure-airport local day.
 * The service provides that day as the YYYY-MM-DD prefix of the localised
 * datetime string. `parseISO` of a no-TZ string parses in the *browser's*
 * local timezone (not UTC), so formatting the result in UTC would shift
 * the calendar day for browsers east/west of UTC and reintroduce the chip-
 * vs-row mismatch. Building a fixed-UTC date from the YYYY-MM-DD prefix
 * makes the rendered day invariant across browsers.
 */
const formatDisplayDate = (dateTime: string | undefined, locale: string) => {
  if (!dateTime) {
    return null;
  }

  const match = displayDatePrefixPattern.exec(dateTime);
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const fixedUTC = parseISO(`${year}-${month}-${day}T00:00:00Z`);

  if (Number.isNaN(fixedUTC.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(fixedUTC);
};

const formatAirportDisplay = (airportName?: string, airportCode?: string) => {
  if (airportName && airportCode) {
    return `${airportName} (${airportCode})`;
  }

  return airportName || airportCode || null;
};

const FlightInfoTableRow = ({
  className = undefined,
  fallbackValue = undefined,
  label,
  value,
}: {
  label: ReactNode;
  value: ReactNode;
  className?: string;
  fallbackValue?: string;
}) => {
  const hasCustomElement =
    value !== null && value !== undefined && typeof value !== 'string';

  return (
    <BpkTableRow>
      <BpkTableCell
        className={classNames(
          STYLES.FlightInfoTable__tableCellLabel,
          className,
        )}
      >
        <BpkText textStyle={TEXT_STYLES.footnote}>{label}</BpkText>
      </BpkTableCell>
      <BpkTableCell
        className={classNames(
          STYLES.FlightInfoTable__tableCellValue,
          className,
        )}
      >
        {hasCustomElement ? (
          value
        ) : (
          <BpkText textStyle={TEXT_STYLES.label2}>
            {value || fallbackValue}
          </BpkText>
        )}
      </BpkTableCell>
    </BpkTableRow>
  );
};

const FlightInfoTable = ({ flight, locale, strings }: FlightInfoTableProps) => {
  if (!flight) {
    return null;
  }

  const departureTimeInfo = getBestDepartureTime(flight, strings, locale);
  const arrivalTimeInfo = getBestArrivalTime(flight, strings, locale);

  return (
    <div className={STYLES.FlightInfoTable}>
      <section className={STYLES.FlightInfoTable__tableSection}>
        <BpkText tagName="h4" textStyle={TEXT_STYLES.heading4}>
          {strings.FlightTrackerFlight_departure_h2}
        </BpkText>
        <BpkTable>
          <BpkTableBody>
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_departure_departs_from_label}
              value={formatAirportDisplay(
                flight.departureAirportName,
                flight.departureAirportCode,
              )}
              className={STYLES['FlightInfoTable__tableCell--first']}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_departure_date_label}
              value={formatDisplayDate(
                getPreferredTimeField(
                  flight,
                  'localisedScheduledDepartureTime',
                  'scheduledDepartureTime',
                ),
                locale,
              )}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            <FlightInfoTableRow
              label={departureTimeInfo.label}
              value={
                departureTimeInfo.isDelayed || departureTimeInfo.isEarly ? (
                  <div className={STYLES.FlightInfoTable__timeWithDelay}>
                    {departureTimeInfo.scheduledTime && (
                      <BpkText
                        textStyle={TEXT_STYLES.label2}
                        className={
                          STYLES.FlightInfoTable__scheduledTimeStrikethrough
                        }
                      >
                        {departureTimeInfo.scheduledTime}
                      </BpkText>
                    )}
                    <BpkText
                      textStyle={TEXT_STYLES.label2}
                      className={
                        departureTimeInfo.isEarly
                          ? STYLES.FlightInfoTable__earlyTime
                          : STYLES.FlightInfoTable__delayedTime
                      }
                    >
                      {departureTimeInfo.value}
                    </BpkText>
                  </div>
                ) : (
                  departureTimeInfo.value
                )
              }
            />
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_departure_terminal_label}
              value={flight.departureTerminal}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_departure_gate_label}
              value={flight.boardingGate}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_departure_checkin_desk_label}
              value={flight.checkInCounter}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
          </BpkTableBody>
        </BpkTable>
      </section>

      <section className={STYLES.FlightInfoTable__tableSection}>
        <BpkText tagName="h4" textStyle={TEXT_STYLES.heading4}>
          {strings.FlightTrackerFlight_arrival_h2}
        </BpkText>
        <BpkTable>
          <BpkTableBody>
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_arrival_arrives_at_label}
              value={
                flight.isDiverted ? (
                  <span
                    className={STYLES.FlightInfoTable__arrivalAirportDiverted}
                  >
                    {formatAirportDisplay(
                      getScheduledArrivalAirportName(flight),
                      getScheduledArrivalAirportCode(flight),
                    )}
                  </span>
                ) : (
                  formatAirportDisplay(
                    flight.arrivalAirportName,
                    flight.arrivalAirportCode,
                  )
                )
              }
              className={STYLES['FlightInfoTable__tableCell--first']}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            {isDivertedFlight(flight) && (
              <BpkTableRow>
                <td colSpan={2} className={STYLES.FlightInfoTable__divertedRow}>
                  <BpkBadge
                    type={BADGE_TYPES.critical}
                    className={STYLES['FlightInfoTable__statusBadge--diverted']}
                  >
                    <span
                      className={STYLES.FlightInfoTable__divertedBadgeWrapper}
                    >
                      <span
                        className={STYLES.FlightInfoTable__divertedBadgeText}
                      >
                        <ExclamationIconSm />
                        &nbsp;
                        {strings.FlightTrackerFlight_arrival_diverted_label}:
                      </span>
                      <BpkText textStyle={TEXT_STYLES.label2}>
                        {formatAirportDisplay(
                          flight.arrivalAirportName,
                          flight.arrivalAirportCode,
                        )}
                      </BpkText>
                    </span>
                  </BpkBadge>
                </td>
              </BpkTableRow>
            )}
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_arrival_date_label}
              value={formatDisplayDate(
                getPreferredTimeField(
                  flight,
                  'localisedScheduledArrivalTime',
                  'scheduledArrivalTime',
                ),
                locale,
              )}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            <FlightInfoTableRow
              label={arrivalTimeInfo.label}
              value={
                <div className={STYLES.FlightInfoTable__tableCellValueWrapper}>
                  {arrivalTimeInfo.isDelayed || arrivalTimeInfo.isEarly ? (
                    <div className={STYLES.FlightInfoTable__timeWithDelay}>
                      {arrivalTimeInfo.scheduledTime && (
                        <BpkText
                          textStyle={TEXT_STYLES.label2}
                          className={
                            STYLES.FlightInfoTable__scheduledTimeStrikethrough
                          }
                        >
                          {arrivalTimeInfo.scheduledTime}
                        </BpkText>
                      )}
                      <BpkText
                        textStyle={TEXT_STYLES.label2}
                        className={
                          arrivalTimeInfo.isEarly
                            ? STYLES.FlightInfoTable__earlyTime
                            : STYLES.FlightInfoTable__delayedTime
                        }
                      >
                        {arrivalTimeInfo.value}
                      </BpkText>
                    </div>
                  ) : (
                    <BpkText textStyle={TEXT_STYLES.label2}>
                      {arrivalTimeInfo.value}
                    </BpkText>
                  )}
                  {flight.status === 'FLIGHT_STATUS_DELAYED' && (
                    <BpkBadge type={BADGE_TYPES.critical}>
                      <ExclamationIconSm />
                      &nbsp;
                      {strings.FlightTrackerFlight_status_value_delayed}
                    </BpkBadge>
                  )}
                </div>
              }
            />
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_arrival_terminal_label}
              value={flight.arrivalTerminal}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_arrival_gate_label}
              value={flight.arrivalGate}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
            <FlightInfoTableRow
              label={strings.FlightTrackerFlight_arrival_luggage_belt_label}
              value={flight.baggageBelt}
              fallbackValue={strings.FlightTrackerFlight_arrival_not_available}
            />
          </BpkTableBody>
        </BpkTable>
      </section>
    </div>
  );
};

export default FlightInfoTable;
